/* =====================================================================
 * rehypeGuideCallout — `> [!NOTE]` 형식의 인용문을 안내 박스로 표시한다.
 *
 * GitHub 과 같은 표기(`[!NOTE]` / `[!TIP]` / `[!WARNING]`)를 쓰므로 저장소에서
 * 문서를 열어도 같은 의미로 읽힌다. 표기 줄은 지우고 `data-callout` 속성만
 * 남겨, 실제 모양은 렌더러의 blockquote 컴포넌트가 정한다.
 * ===================================================================== */

import type { Element, ElementContent, Root, RootContent } from 'hast';

export type GuideCalloutKind = 'note' | 'tip' | 'warning';

const MARKER = /^\s*\[!(NOTE|TIP|WARNING)\]\s*\n?/i;

function isElement(node: RootContent | ElementContent): node is Element {
  return node.type === 'element';
}

function applyCallout(blockquote: Element): void {
  const firstParagraph = blockquote.children.find(
    (child): child is Element => isElement(child) && child.tagName === 'p',
  );
  const firstText = firstParagraph?.children[0];
  if (!firstParagraph || !firstText || firstText.type !== 'text') return;

  const match = MARKER.exec(firstText.value);
  if (!match) return;

  firstText.value = firstText.value.slice(match[0].length);
  /* 표기 뒤 줄바꿈이 `<br>` 로 남는 경우가 있어 앞쪽 빈 조각을 정리한다. */
  while (firstParagraph.children.length > 0) {
    const head = firstParagraph.children[0];
    const emptyText = head.type === 'text' && head.value.trim() === '';
    const lineBreak = isElement(head) && head.tagName === 'br';
    if (!emptyText && !lineBreak) break;
    firstParagraph.children.shift();
  }
  if (firstParagraph.children.length === 0) {
    blockquote.children = blockquote.children.filter((child) => child !== firstParagraph);
  }

  blockquote.properties = {
    ...blockquote.properties,
    dataCallout: match[1].toLowerCase() as GuideCalloutKind,
  };
}

function walk(nodes: Array<RootContent | ElementContent>): void {
  for (const node of nodes) {
    if (!isElement(node)) continue;
    if (node.tagName === 'blockquote') applyCallout(node);
    walk(node.children);
  }
}

export function rehypeGuideCallout() {
  return (tree: Root) => {
    walk(tree.children);
  };
}
